import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { calcTestSignature } from '@lemoncloud/lemon-web-core';

import { GAEvents, trackEvent } from '@codes/analytics';
import { CodesLogoImage, LoginCoverDarkImage, LoginCoverImage, LoginGoogleIcon, LogoLightImage } from '@codes/assets';
import { VerifyNativeTokenBody, useSnsTestLoginQuery } from '@codes/auth';
import { useLoader } from '@codes/loader';
import { Input } from '@codes/ui-kit';
import { isProdEnvironment, useWebCoreStore } from '@codes/web-core';

interface LocationState {
    from?: string;
}

export const LoginPage = () => {
    const { setIsLoading } = useLoader();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as LocationState | null;

    const { setIsAuthenticated, setProfile } = useWebCoreStore(state => state);

    const onClickLogin = (provider: string) => {
        trackEvent(GAEvents.LOGIN_CLICK, { method: provider });
        setIsLoading(true);
        const HOST = import.meta.env.VITE_HOST.toLowerCase();
        const SOCIAL_OAUTH = import.meta.env.VITE_SOCIAL_OAUTH_ENDPOINT.toLowerCase();
        const redirectUrl = `${HOST}/auth/oauth-response`;

        // Include original URL in state parameter for redirect after login
        const stateObj = { from: locationState?.from || '/w' };
        const stateParam = encodeURIComponent(JSON.stringify(stateObj));

        window.location.replace(
            `${SOCIAL_OAUTH}/oauth/${provider}/authorize?redirect=${redirectUrl}&state=${stateParam}`
        );
    };

    const { mutateAsync: snsTestLogin } = useSnsTestLoginQuery(
        _ => {
            setIsAuthenticated(true);
            // Redirect to original URL if available
            const redirectTo = locationState?.from || '/w';
            navigate(redirectTo, { replace: true });
            setIsLoading(false);
        },
        e => {
            confirm(e?.message || '에러입니다.');
            setIsLoading(false);
        }
    );

    const isProd = isProdEnvironment();

    const [snsTestUid, setSnsTestUid] = useState('');
    const isDisabled = !snsTestUid;

    const onClickSnsTestLogin = () => {
        const date = new Date();

        const body: VerifyNativeTokenBody = {
            provider: 'test',
            idToken: snsTestUid,
            refreshToken: date.toISOString(),
            signature: calcTestSignature(
                {
                    authId: snsTestUid,
                    accountId: snsTestUid,
                    identityId: snsTestUid,
                    identityToken: '*test-native-token-240828*',
                },
                date.toISOString()
            ),
        };
        setIsLoading(true);
        snsTestLogin(body);
    };

    const onEnterSnsTestInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter' || !snsTestLogin) {
            return;
        }

        onClickSnsTestLogin();
    };

    return (
        <div className="w-screen h-screen grid grid-cols-2 max-md:grid-cols-1 p-6 items-center justify-center bg-background">
            <div className="h-full justify-self-center w-full flex flex-col items-center max-w-[420px] max-h-[900px] py-20">
                <div className="w-full flex flex-col items-center md:pt-[60%]">
                    <Image className="h-10" src={LogoLightImage} darkSrc={CodesLogoImage} />
                    <div className="mt-5 mb-16 text-sub-title text-xl font-medium whitespace-nowrap">
                        {t('auth.social_login')}
                    </div>
                    <div className="w-full flex flex-col gap-5">
                        <button
                            className="h-[54px] rounded-full border border-border shadow-custom hover:shadow-card-hover transition-shadow"
                            onClick={() => onClickLogin('google')}
                        >
                            <div className="px-7 flex items-center justify-center">
                                <Image className="ml-auto" src={LoginGoogleIcon} />
                                <div className="flex-1 pr-5 font-medium">{t('auth.google_login')}</div>
                            </div>
                        </button>
                        {/* TODO:kakao login button */}
                        {/* <button className="h-[54px] rounded-[34px] border border-gradient shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)]">
                        <div className="px-7 flex items-center justify-center">
                            <Image className="ml-auto" src={LoginKakaoIcon} />
                            <div className="flex-1 pr-5 font-medium">{t('auth.kakao_login')}</div>
                        </div>
                    </button> */}
                    </div>
                    {!isProd && (
                        <div className="w-full flex flex-col gap-3 mt-11">
                            <Input
                                type="text"
                                className="border p-3 rounded-[5px]"
                                placeholder="아무거나 입력해주세요"
                                value={snsTestUid}
                                onChange={e => setSnsTestUid(e.target.value)}
                                onKeyDown={onEnterSnsTestInput}
                            />
                            <button
                                className={`w-full py-3 bg-primary font-bold text-white rounded-lg ${
                                    isDisabled ? 'bg-button-disabled' : 'hover:bg-primary-hover'
                                } transition-all`}
                                disabled={isDisabled}
                                onClick={onClickSnsTestLogin}
                            >
                                SNS-TEST LOGIN
                            </button>
                        </div>
                    )}
                </div>

                <div className="text-lg text-text-500 md:mt-auto mt-10">
                    From Infrastructure to Scalable Microservices
                </div>
            </div>
            <Image
                className="max-md:hidden h-full max-h-[900px] object-contain"
                src={LoginCoverImage}
                darkSrc={LoginCoverDarkImage}
            />
        </div>
    );
};
